<template>
    <main class="dashboard panel-analista">
        <MainLayout
            :chartLoaded="true"
            @change-section="onSectionChange"
        />

        <div class="main-section underHeader">
            <div class="panel-shell">
                <!-- Encabezado: contexto + acciones. El analista llega acá para
                     ver de un vistazo qué clientes necesitan atención. -->
                <header class="panel-head">
                    <div class="panel-head__intro">
                        <p class="panel-eyebrow">Panel analista</p>
                        <h1 class="panel-title">Cartera de clientes</h1>
                        <p class="panel-sub">
                            <template v-if="numberRegisters">
                                {{ numberRegisters }} empresas — revisá la salud de datos de cada una.
                            </template>
                            <template v-else>Tus empresas asignadas.</template>
                        </p>
                    </div>
                    <div class="panel-actions">
                        <button
                            v-if="siiProblemsCount"
                            type="button"
                            class="pill pill--filter"
                            :class="{ 'is-active': filterSiiProblems }"
                            @click="filterSiiProblems = !filterSiiProblems"
                        >
                            SII con problemas
                            <span class="pill__badge">{{ siiProblemsCount }}</span>
                        </button>
                        <button type="button" class="pill" @click="asignarAnalistas">
                            Asignar analistas
                        </button>
                        <button type="button" class="pill" @click="agregarCompania">
                            Agregar compañía
                        </button>
                        <button type="button" class="pill" @click="verPlanesEstandar">
                            Planes estándar
                        </button>
                        <button type="button" class="pill pill--accent" @click="verSolicitudes">
                            Solicitudes
                            <span v-if="pendingRequestsCount" class="pill__badge">
                                {{ pendingRequestsCount }}
                            </span>
                        </button>
                    </div>
                </header>

                <!-- Cartera -->
                <div class="panel-card">
                    <div class="cartera-toolbar">
                        <div class="search-box">
                            <input
                                v-model="searchQuery"
                                type="search"
                                class="search-box__input"
                                placeholder="Buscar por empresa, RUT o usuario…"
                                aria-label="Buscar en la cartera"
                            />
                            <button
                                v-if="searchQuery"
                                type="button"
                                class="search-box__clear"
                                aria-label="Limpiar búsqueda"
                                @click="searchQuery = ''"
                            >×</button>
                        </div>
                        <span v-if="searchQuery" class="cartera-toolbar__count">
                            {{ filteredData.length }} de {{ carteraClientes.length }} empresas
                        </span>
                    </div>
                    <b-table
                        :data="sortedData"
                        :loading="tableData.loading"
                        ref="table"
                        :total="numberRegisters"
                        :show-detail-icon="false"
                        class="cartera-table"
                    >
                        <template #empty>
                            <div class="cartera-empty">
                                <template v-if="searchQuery">
                                    Sin resultados para «{{ searchQuery }}».
                                </template>
                                <template v-else>
                                    No hay clientes en tu cartera todavía.
                                </template>
                            </div>
                        </template>

                        <b-table-column field="company_name" cell-class="col col--company">
                            <template #header>
                                <button class="col-head" @click="changeSortCompanyName">
                                    Compañía <span class="col-head__sort">{{ orderMsg['company_name'] }}</span>
                                </button>
                            </template>
                            <template #default="{ row }">
                                <button
                                    class="company-link"
                                    :class="{ 'is-mine': row.username === analystName }"
                                    @click="openCompany(row)"
                                >
                                    {{ row.company_name }}
                                </button>
                            </template>
                        </b-table-column>

                        <b-table-column field="company_type" cell-class="col col--type">
                            <template #header>
                                <button class="col-head" @click="changeSortCompanyType">
                                    Tipo <span class="col-head__sort">{{ orderMsg['company_type'] }}</span>
                                </button>
                            </template>
                            <template #default="{ row }">
                                <span class="type-tag">{{ row.company_type || '—' }}</span>
                            </template>
                        </b-table-column>

                        <b-table-column field="username" cell-class="col col--analyst">
                            <template #header>
                                <button class="col-head" @click="changeSortAnalista">
                                    Analista <span class="col-head__sort">{{ orderMsg['nombre'] }}</span>
                                </button>
                            </template>
                            <template #default="{ row }">
                                <span class="analyst" :class="{ 'is-mine': row.username === analystName }">
                                    {{ row.nombre }}
                                </span>
                            </template>
                        </b-table-column>

                        <!-- Firma del panel: la "tira de salud" — pills semánticos
                             que se leen de un vistazo por columna. -->
                        <b-table-column field="completion" cell-class="col col--health">
                            <template #header>
                                <button class="col-head" @click="changeSortStatus">
                                    Estado de datos <span class="col-head__sort">{{ orderMsg['completion'] }}</span>
                                </button>
                            </template>
                            <template #default="{ row }">
                                <div class="health">
                                    <!-- Semáforo SII: 5 estados derivados en el backend
                                         (sii_status_selector); el detalle viaja en el title. -->
                                    <span
                                        class="chip"
                                        :class="siiChip(row).cls"
                                        :title="row.company_sii_detail || ''"
                                    >
                                        <span class="chip__dot"></span> {{ siiChip(row).label }}
                                    </span>
                                    <span class="chip" :class="row.company_has_account_plans ? 'is-ok' : 'is-off'">
                                        <span class="chip__dot"></span> {{ row.company_n_account_plans }} plan de cuentas
                                    </span>
                                    <span class="chip" :class="row.company_has_categories ? 'is-ok' : 'is-off'">
                                        <span class="chip__dot"></span> {{ row.company_n_categories }} categorías
                                    </span>
                                </div>
                            </template>
                        </b-table-column>

                        <b-table-column field="last_data" cell-class="col col--sources">
                            <template #header>
                                <button class="col-head" @click="changeSortLastRecord">
                                    Fuentes <span class="col-head__sort">{{ orderMsg['last_data'] }}</span>
                                </button>
                            </template>
                            <template #default="{ row }">
                                <div class="sources">
                                    <span class="source is-ok">
                                        <b>{{ row.datasources_good != null ? row.datasources_good : '·' }}</b> ok
                                    </span>
                                    <span class="source" :class="row.datasources_bad ? 'is-bad' : 'is-muted'">
                                        <b>{{ row.datasources_bad != null ? row.datasources_bad : '·' }}</b> problemas
                                    </span>
                                </div>
                            </template>
                        </b-table-column>

                        <b-table-column field="classification" cell-class="col col--classif">
                            <template #header>
                                <span class="col-head col-head--static">Clasificación</span>
                            </template>
                            <template #default="{ row }">
                                <span :id="'last_data_' + row.company_id" class="classif-slot"></span>
                            </template>
                        </b-table-column>
                    </b-table>
                </div>
            </div>
        </div>
    </main>
</template>

<script>
import MainLayout from "@/components/layout/MainLayout";
import AddCompany from "@/components/modals/AddCompany";
import AssignAnalysts from "@/components/modals/AssignAnalysts";
import LinkRequestsQueue from "@/components/modals/LinkRequestsQueue";

import { mapActions } from "vuex";

import { consoleHandlerError, notificate } from "@/utils/message";

export default {
    name: "PanelAnalista",
    components: {
        MainLayout,
    },
    data() {
        return {
            pendingRequestsCount: 0,
            filterSiiProblems: false,
            searchQuery: "",
            sortCycle: 0,
            analystName: '',
            carteraClientes: [],
            tableData: {
                loading: true,
                page: 0,
            },
            numberRegisters: 0,
            sortColumn: null,
            sortDirection: null,
            orderMsg: {
                'nombre': '↕',
                'company_rut': '↕',
                'company_name': '↕',
                'bigger_email': '↕',
                'company_type': '↕',
                'completion': '↕',
                'last_data': '↕',
            },
            modalDefaultUpdateSheet: {
                parent: this,
                hasModalCard: true,
                customClass: "modal-class",
                canCancel: ["escape", "outside"],
            },
        };
    },
    mounted() {
        this.loadData();
        this.loadPendingRequestsCount();
    },
    computed: {
        siiProblemsCount() {
            return this.carteraClientes.filter(
                (row) => row.company_sii_status === "BROKEN" || row.company_sii_status === "LIKELY_BROKEN"
            ).length;
        },
        searchedData() {
            // Case- y tilde-insensitive: "panaderia" matchea "Panadería".
            const fold = (value) =>
                String(value || "")
                    .toLowerCase()
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, "");
            const query = fold(this.searchQuery.trim());
            if (!query) {
                return this.carteraClientes;
            }
            // El RUT se compara sin puntos/guiones/espacios en ambos lados:
            // "90.002.865-2", "90002865-2" y "90002865" matchean igual.
            const rutQuery = query.replace(/[.\s-]/g, "");
            return this.carteraClientes.filter((row) => {
                const textFields = [
                    row.company_name,
                    row.bigger_email,
                    row.company_contact,
                    row.nombre,
                    row.username,
                ];
                if (textFields.some((value) => fold(value).includes(query))) {
                    return true;
                }
                const rut = fold(row.company_rut).replace(/[.\s-]/g, "");
                return Boolean(rutQuery) && rut.includes(rutQuery);
            });
        },
        filteredData() {
            if (!this.filterSiiProblems) {
                return this.searchedData;
            }
            return this.searchedData.filter(
                (row) => row.company_sii_status === "BROKEN" || row.company_sii_status === "LIKELY_BROKEN"
            );
        },
        sortedData() {
            if (!this.sortColumn) {
                return this.filteredData;
            }
            return [...this.filteredData].sort((a, b) => {
                var aval = a[this.sortColumn];
                var bval = b[this.sortColumn];
                aval = String(aval).toLowerCase();
                bval = String(bval).toLowerCase();
                if (aval < bval) {
                    return this.sortDirection === 'asc' ? -1 : 1;
                }
                if (aval > bval) {
                    return this.sortDirection === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
    },
    methods: {
        ...mapActions("analystCompany", ["fetchCartera", "fetchLinkRequests"]),
        siiChip(row) {
            // Semáforo SII (company_sii_status del backend). Fallback al booleano
            // legacy company_has_sii_fintoc si el payload viejo sigue cacheado.
            const chips = {
                OK: { cls: "is-ok", label: "SII ok" },
                CONNECTING: { cls: "is-progress", label: "SII conectando" },
                LIKELY_BROKEN: { cls: "is-warn", label: "SII a revisar" },
                BROKEN: { cls: "is-bad", label: "SII con problemas" },
                NOT_APPLICABLE: { cls: "is-off", label: "Sin SII" },
            };
            return (
                chips[row.company_sii_status] ||
                (row.company_has_sii_fintoc ? chips.OK : chips.NOT_APPLICABLE)
            );
        },
        loadData() {
            this.tableData.loading = true;
            // Se retorna la chain (post-catch: nunca rechaza) para que los
            // tests puedan await-earla; los callers la ignoran.
            return this.fetchCartera().then((data) => {
                this.analystName = data.analystName;
                this.numberRegisters = data.count;
                // get_all ya trae el enriquecimiento por-compañía (tipo,
                // contacto, completitud, datasources); no hay que pedir
                // complementary_data por cada fila.
                // Reemplazo (no push): los modales recargan al cerrar y el
                // push duplicaba las filas en cada recarga.
                this.carteraClientes = [...data.objects];
            }).catch((error) => {
                // silent: el modo default de consoleHandlerError re-lanza, y
                // acá nadie más consume la promise — volvería a ser una
                // unhandled rejection (CFO_REMOTO_FRONTEND-9S).
                consoleHandlerError({ error, prefix: "[panelAnalista]", silent: true });
                notificate(
                    "No pudimos cargar tu cartera de clientes. Revisá tu conexión e intentá de nuevo.",
                    "is-danger is-light"
                );
            }).finally(() => {
                this.tableData.loading = false;
            });
        },
        agregarCompania() {
            this.$buefy.modal.open({
                ...this.modalDefaultUpdateSheet,
                component: AddCompany,
                events: {
                    close: () => {
                        this.loadData();
                    }
                },
                onCancel: () => {
                    this.loadData();
                },
                onClose: () => {
                    this.loadData();
                }
            });
        },
        asignarAnalistas() {
            this.$buefy.modal.open({
                ...this.modalDefaultUpdateSheet,
                component: AssignAnalysts,
                props: {
                    currentAnalystEmail: this.analystName,
                },
                events: {
                    close: () => {
                        this.loadData();
                    }
                },
                onCancel: () => {
                    this.loadData();
                },
                onClose: () => {
                    this.loadData();
                },
            });
        },
        verPlanesEstandar() {
            this.$router.push({ name: "standardAccountPlans" });
        },
        loadPendingRequestsCount() {
            this.fetchLinkRequests().then((data) => {
                this.pendingRequestsCount = data.requests.length;
            }).catch(() => {
                this.pendingRequestsCount = 0;
            });
        },
        verSolicitudes() {
            this.$buefy.modal.open({
                ...this.modalDefaultUpdateSheet,
                component: LinkRequestsQueue,
                events: {
                    close: () => {
                        this.loadPendingRequestsCount();
                    }
                },
                onCancel: () => {
                    this.loadPendingRequestsCount();
                },
                onClose: () => {
                    this.loadPendingRequestsCount();
                },
            });
        },
        resetMsgs() {
            for (var key in this.orderMsg) {
                this.orderMsg[key] = '↕';
            }
        },
        changeOrderGeneric(column) {
            // Ciclo de orden en la instancia (antes era un `let count` a nivel
            // módulo: compartido entre remounts, se desincronizaba de
            // sortColumn al volver a la vista).
            if (this.sortColumn != column) {
                this.sortCycle = 0;
                this.sortColumn = column;
            }
            this.sortCycle = (this.sortCycle + 1) % 3;
            this.resetMsgs();
            if (this.sortCycle == 0) {
                this.sortColumn = null;
                this.orderMsg[column] = '↕'
            } else if (this.sortCycle == 1) {
                this.sortColumn = column;
                this.sortDirection = 'asc';
                this.orderMsg[column] = '↑';
            } else if (this.sortCycle == 2) {
                this.sortColumn = column;
                this.sortDirection = 'desc';
                this.orderMsg[column] = '↓';
            }
        },
        changeSortAnalista() {
            this.changeOrderGeneric('nombre');
        },
        changeSortCompanyName() {
            this.changeOrderGeneric('company_name');
        },
        changeSortCompanyType() {
            this.changeOrderGeneric('company_type');
        },
        changeSortStatus() {
            this.changeOrderGeneric('completion');
        },
        changeSortLastRecord() {
            this.changeOrderGeneric('last_data');
        },
        openCompany(companySelected) {
            this.$router.push({
                name: "companyData",
                query: {
                    companyId: companySelected.company_id,
                    companyRut: companySelected.company_rut
                },
            })
        },
        onSectionChange(section, tab) {
            // PanelAnalista no es una sección del sidebar — cualquier click en el
            // rail navega al Dashboard con el query correspondiente.
            const query = { section };
            if (tab) query.tab = tab;
            this.$router.push({ path: "/dashboard", query }).catch((err) => {
                if (err.name !== "NavigationDuplicated") throw err;
            });
        },
    },
}
</script>

<style scoped lang="scss">
// ============================================================================
// Panel Analista — acotado a esta vista (NO toca Bulma global). Consume el tema
// compartido del analista (var(--ap-*), en elements/_analyst_theme.scss) para
// que el panel y sus modales usen la MISMA paleta/tipografía. Tipografía del
// proyecto (Inter / fallback Open Sans). Look de Bulma sobrescrito en scope.
// ============================================================================
.panel-analista {
    font-family: var(--ap-font);
    color: var(--ap-ink-2);
}

.panel-shell {
    max-width: 1240px;
    margin: 0 auto;
    padding: 32px 28px 56px;
}

// Encabezado ----------------------------------------------------------------
.panel-head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 24px;
    flex-wrap: wrap;
    margin-bottom: 24px;
}

.panel-eyebrow {
    margin: 0 0 6px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--ap-teal);
}

.panel-title {
    margin: 0;
    font-family: var(--ap-font);
    font-weight: 700;
    font-size: 30px;
    line-height: 1.05;
    letter-spacing: -0.02em;
    color: var(--ap-ink);
}

.panel-sub {
    margin: 8px 0 0;
    font-size: 14px;
    color: var(--ap-slate);
}

.panel-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

// Pills de acción (micro-interacción sutil: eleva 1px y tiñe el borde) --------
.pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 18px;
    border: 1px solid var(--ap-line-strong);
    border-radius: var(--ap-radius-pill);
    background-color: var(--ap-paper);
    color: var(--ap-ink-2);
    font-family: var(--ap-font);
    font-size: 13.5px;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.16s var(--ap-ease), border-color 0.16s var(--ap-ease),
        background-color 0.16s var(--ap-ease), box-shadow 0.16s var(--ap-ease);

    &:hover {
        transform: translateY(-1px);
        border-color: var(--ap-teal);
        color: var(--ap-teal-deep);
        box-shadow: 0 6px 18px -10px rgba(14, 47, 52, 0.5);
    }

    &:active {
        transform: translateY(0);
    }

    &:focus-visible {
        outline: 2px solid var(--ap-teal);
        outline-offset: 2px;
    }
}

// Filtro "SII con problemas": pill con estado activo persistente.
.pill--filter {
    &.is-active {
        border-color: var(--ap-alert);
        background-color: var(--ap-alert-wash);
        color: var(--ap-alert);
    }
}

.pill--accent {
    border-color: transparent;
    background-color: var(--ap-ink);
    color: var(--ap-paper);

    &:hover {
        color: var(--ap-paper);
        background-color: var(--ap-teal-deep);
        border-color: transparent;
    }
}

.pill__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: var(--ap-radius-pill);
    background-color: var(--ap-alert);
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
}

// Card contenedor de la tabla -----------------------------------------------
.panel-card {
    background-color: var(--ap-paper);
    border: 1px solid var(--ap-line);
    border-radius: var(--ap-radius);
    box-shadow: var(--ap-shadow-card);
    overflow: hidden;
}

// Toolbar de la cartera: buscador + contador de resultados -------------------
.cartera-toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border-bottom: 1px solid var(--ap-line);
}

.search-box {
    position: relative;
    flex: 0 1 340px;
}

.search-box__input {
    width: 100%;
    padding: 8px 32px 8px 12px;
    font-family: var(--ap-font);
    font-size: 14px;
    color: var(--ap-ink-2);
    background-color: var(--ap-paper);
    border: 1px solid var(--ap-line);
    border-radius: var(--ap-radius-pill);
    transition: border-color 0.16s var(--ap-ease), box-shadow 0.16s var(--ap-ease);

    &::placeholder {
        color: var(--ap-ink-3, #98a2b3);
    }

    &:focus {
        outline: none;
        border-color: var(--ap-teal);
        box-shadow: 0 0 0 3px rgba(14, 116, 144, 0.12);
    }
}

.search-box__clear {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: var(--ap-ink-2);
    font-size: 16px;
    line-height: 1;
    cursor: pointer;

    &:hover {
        background-color: var(--ap-line);
    }
}

.cartera-toolbar__count {
    font-size: 13px;
    color: var(--ap-ink-2);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

// Reset del cromo de Bulma dentro del scope + look propio de la tabla --------
:deep(.cartera-table){
    font-family: var(--ap-font);

    .table {
        background-color: transparent;
        color: var(--ap-ink-2);
        width: 100%;
    }

    // Cabecera: eyebrow discreto, sin el th default de Bulma.
    thead th {
        border: none;
        border-bottom: 1px solid var(--ap-line);
        background-color: var(--ap-mist);
        padding: 0;
        vertical-align: middle;
    }

    tbody td {
        border: none;
        border-bottom: 1px solid var(--ap-line);
        padding: 16px 18px;
        vertical-align: middle;
        font-size: 13.5px;
    }

    tbody tr:last-child td {
        border-bottom: none;
    }

    // Micro-interacción: la fila se tiñe al hover (escaneo cómodo).
    tbody tr {
        transition: background-color 0.14s var(--ap-ease);
    }

    tbody tr:hover {
        background-color: var(--ap-teal-wash);
    }
}

// Cabeceras (botón de orden) ------------------------------------------------
:deep(.col-head){
    display: inline-flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    padding: 13px 18px;
    border: none;
    background: transparent;
    cursor: pointer;
    font-family: var(--ap-font);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--ap-muted);
    transition: color 0.14s var(--ap-ease);

    &:hover {
        color: var(--ap-ink);
    }
}

:deep(.col-head--static){
    cursor: default;

    &:hover {
        color: var(--ap-muted);
    }
}

:deep(.col-head__sort){
    font-size: 12px;
    color: var(--ap-slate);
}

// Anchos por columna (acotados, sin los overrides globales del original) -----
:deep(.col--company){
    width: 22%;
    min-width: 180px;
}

:deep(.col--type){
    width: 8%;
    white-space: nowrap;
}

:deep(.col--analyst){
    width: 14%;
}

:deep(.col--health){
    width: 30%;
    min-width: 260px;
}

:deep(.col--sources){
    width: 14%;
    white-space: nowrap;
}

// Nombre de empresa: ancla clickeable ---------------------------------------
:deep(.company-link){
    border: none;
    background: transparent;
    padding: 0;
    text-align: left;
    font-family: var(--ap-font);
    font-size: 14px;
    font-weight: 600;
    color: var(--ap-ink);
    cursor: pointer;
    border-bottom: 1px solid transparent;
    transition: color 0.14s var(--ap-ease), border-color 0.14s var(--ap-ease);

    &:hover {
        color: var(--ap-teal-deep);
        border-bottom-color: rgba(13, 125, 128, 0.4);
    }

    &.is-mine {
        color: var(--ap-teal-deep);
    }
}

// Tipo: tag neutro y disciplinado (la firma está en el estado, no acá) -------
:deep(.type-tag){
    display: inline-block;
    padding: 3px 10px;
    border: 1px solid var(--ap-line-strong);
    border-radius: var(--ap-radius-pill);
    background-color: var(--ap-mist);
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: var(--ap-slate);
}

:deep(.analyst){
    color: var(--ap-slate);
    font-size: 13px;

    &.is-mine {
        color: var(--ap-ink);
        font-weight: 600;
    }
}

// FIRMA: tira de salud — pills semánticos que se leen de un vistazo ----------
:deep(.health){
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

:deep(.chip){
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: var(--ap-radius-pill);
    font-size: 11.5px;
    font-weight: 500;
    white-space: nowrap;
    border: 1px solid transparent;
    font-variant-numeric: tabular-nums;

    .chip__dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        flex-shrink: 0;
    }

    &.is-ok {
        background-color: var(--ap-ok-wash);
        color: var(--ap-ok-ink);
        border-color: rgba(15, 157, 107, 0.22);

        .chip__dot {
            background-color: var(--ap-ok);
        }
    }

    &.is-off {
        background-color: var(--ap-mist);
        color: var(--ap-muted);
        border-color: var(--ap-line);

        .chip__dot {
            background-color: #c4d0d1;
        }
    }

    // Semáforo SII: conectando / a revisar / con problemas.
    &.is-progress {
        background-color: var(--ap-teal-wash);
        color: var(--ap-teal-deep);
        border-color: rgba(13, 125, 128, 0.25);

        .chip__dot {
            background-color: var(--ap-teal);
        }
    }

    &.is-warn {
        background-color: var(--ap-warn-wash);
        color: var(--ap-warn-ink);
        border-color: rgba(200, 138, 0, 0.25);

        .chip__dot {
            background-color: var(--ap-warn);
        }
    }

    &.is-bad {
        background-color: var(--ap-alert-wash);
        color: var(--ap-alert);
        border-color: rgba(192, 69, 59, 0.25);

        .chip__dot {
            background-color: var(--ap-alert);
        }
    }
}

// Fuentes de datos: buenas / con problemas ----------------------------------
:deep(.sources){
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 12.5px;
    color: var(--ap-slate);
    font-variant-numeric: tabular-nums;

    b {
        font-weight: 700;
    }

    .source.is-ok b {
        color: var(--ap-ok);
    }

    .source.is-bad b {
        color: var(--ap-alert);
    }

    .source.is-muted b {
        color: var(--ap-muted);
    }
}

:deep(.classif-slot){
    font-size: 12.5px;
    color: var(--ap-slate);
}

.cartera-empty {
    padding: 48px 0;
    text-align: center;
    color: var(--ap-muted);
    font-size: 14px;
}

@media (prefers-reduced-motion: reduce) {
    .pill,
    :deep(.cartera-table tbody tr),
    :deep(.company-link),
    :deep(.col-head){
        transition: none;
    }
}
</style>
